import { useState, useEffect } from 'react';
import { Calendar, Users, Search, Check, Tag as TagIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BookingSearchModal = ({
    isOpen,
    onClose,
    onSearch,
    initialCheckIn,
    initialCheckOut,
    initialGuests = 2,
    useExternalPromo = false
}) => {
    const [checkIn, setCheckIn] = useState(initialCheckIn || new Date());
    const [checkOut, setCheckOut] = useState(initialCheckOut || null);
    const [guests, setGuests] = useState(initialGuests);
    const [promoCode, setPromoCode] = useState('');
    const [verifiedPromo, setVerifiedPromo] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        if (initialCheckIn) setCheckIn(initialCheckIn);
        if (initialCheckOut) setCheckOut(initialCheckOut);
    }, [initialCheckIn, initialCheckOut]);

    // Ensure checkout is set when checkin changes if invalid
    const handleCheckInSelect = (date) => {
        setCheckIn(date);
        if (date) {
            const nextDay = new Date(date);
            nextDay.setDate(date.getDate() + 1);
            setCheckOut(nextDay);
        }
    };

    const verifyPromoCode = async () => {
        if (!promoCode) return;
        if (useExternalPromo) {
            setVerifiedPromo(null);
            toast.info('Kode promo akan diverifikasi saat pencarian');
            return;
        }
        setIsVerifying(true);
        try {
            const response = await axios.post(`${API_URL}/promo/verify`, {
                code: promoCode,
                check_in: format(checkIn, 'yyyy-MM-dd')
            });

            if (response.data.valid) {
                setVerifiedPromo(response.data);
                toast.success(response.data.message || 'Kode promo berhasil digunakan!');
            }
        } catch (error) {
            setVerifiedPromo(null);
            toast.error(error.response?.data?.detail || 'Kode promo tidak valid');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleSearch = async () => {
        if (!checkIn || !checkOut) return;
        setIsSearching(true);

        // Construct search data
        const searchData = {
            checkIn,
            checkOut,
            guests,
            promoCode,
            verifiedPromo
        };

        try {
            if (onSearch) {
                await onSearch(searchData);
            }
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl p-6">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl mb-4">Find Your Stay</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                    {/* Check-in */}
                    <div>
                        <Label className="text-gray-600 mb-1.5 block text-sm">Check-in</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-full justify-start text-left font-normal h-12 text-sm",
                                        !checkIn && "text-muted-foreground"
                                    )}
                                >
                                    <Calendar className="mr-2 h-4 w-4 text-emerald-600 flex-shrink-0" />
                                    <span className="truncate">{checkIn ? format(checkIn, "dd MMM yyyy") : "Select date"}</span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                    mode="single"
                                    selected={checkIn}
                                    onSelect={handleCheckInSelect}
                                    disabled={(date) => date < new Date().setHours(0, 0, 0, 0)}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Check-out */}
                    <div>
                        <Label className="text-gray-600 mb-1.5 block text-sm">Check-out</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-full justify-start text-left font-normal h-12 text-sm",
                                        !checkOut && "text-muted-foreground"
                                    )}
                                >
                                    <Calendar className="mr-2 h-4 w-4 text-emerald-600 flex-shrink-0" />
                                    <span className="truncate">{checkOut ? format(checkOut, "dd MMM yyyy") : "Select date"}</span>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                    mode="single"
                                    selected={checkOut}
                                    onSelect={setCheckOut}
                                    disabled={(date) => date <= checkIn}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Guests */}
                    <div>
                        <Label className="text-gray-600 mb-1.5 block text-sm">Guests</Label>
                        <div className="relative">
                            <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />
                            <select
                                value={guests}
                                onChange={(e) => setGuests(Number(e.target.value))}
                                className="w-full h-12 pl-10 pr-4 border rounded-lg appearance-none bg-white focus:ring-2 focus:ring-emerald-500 text-sm"
                            >
                                {[1, 2, 3, 4].map(n => (
                                    <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Promo Code */}
                    <div>
                        <Label className="text-gray-600 mb-1.5 block text-sm">Promo Code</Label>
                        <div className="relative flex">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                <TagIcon className="h-4 w-4" />
                            </div>
                            <input
                                type="text"
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}
                                placeholder="Kode Promo"
                                className="w-full h-12 pl-10 pr-20 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm uppercase"
                            />
                            <button
                                onClick={verifyPromoCode}
                                disabled={isVerifying || !promoCode}
                                className="absolute right-1 top-1 bottom-1 px-3 bg-emerald-50 text-emerald-600 rounded-md text-xs font-medium hover:bg-emerald-100 disabled:opacity-50"
                            >
                                {isVerifying ? '...' : verifiedPromo ? <Check className="w-4 h-4" /> : useExternalPromo ? 'Apply' : 'Verify'}
                            </button>
                        </div>
                        {verifiedPromo && (
                            <p className="text-[10px] text-emerald-600 mt-1 absolute font-medium">
                                {verifiedPromo.discount_type === 'manual_price'
                                    ? `Harga Rp ${verifiedPromo.discount_value.toLocaleString('id-ID')}/malam`
                                    : `Hemat ${verifiedPromo.discount_type === 'percent' ? `${verifiedPromo.discount_value}%` : `Rp ${verifiedPromo.discount_value.toLocaleString('id-ID')}`}`}
                            </p>
                        )}
                    </div>

                    {/* Search Button */}
                    <div className="col-span-2 lg:col-span-1">
                        <Button
                            onClick={handleSearch}
                            disabled={isSearching}
                            className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-base w-full"
                        >
                            {isSearching ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                            ) : (
                                <>
                                    <Search className="mr-2 h-4 w-4" />
                                    Search
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default BookingSearchModal;
